import { ignoreAbortError } from '@/utils/abort-util'
import { type TeamRoleStore } from '@/features/iam/teams/TeamRoleStore'
import { type TeamMemberStore } from '@/features/iam/teams/TeamMemberStore'
import { type GetCurrentMemberFn } from '@/features/iam'
import { type Team } from '@/features/iam/teams/Team'
import { type InvitationStore } from '@/features/iam/invitations/InvitationStore'
import { task } from 'mobx-task'
import { computed, action, observable } from 'mobx'
import { type TeamMember } from '@/features/iam/teams/TeamMember'
import { type TableSortOrder } from '@/components/Table'
import { orderBy } from 'lodash-es'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { InviteTeamMemberModalViewModel } from '@/features/iam/teams/InviteTeamMemberModal/InviteTeamMemberModalViewModel'
import { PendingTeamMembersViewModel } from './PendingTeamMembersViewModel'
import { RemoveTeamMemberDialogViewModel } from './RemoveTeamMemberDialog/RemoveTeamMemberDialogViewModel'

export type TeamMemberKind = 'Current' | 'Pending'

export type TeamMemberTableColumnKey =
  'member.displayName' | 'teamRole.name' | 'dateCreated'

export class TeamMembersViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable accessor searchText = ''
  @observable accessor sortOrder: TableSortOrder = 'none'
  @observable accessor sortField: TeamMemberTableColumnKey =
    'member.displayName'

  @observable accessor visibleKind: TeamMemberKind = 'Current'

  readonly inviteTeamMemberModalVm: InviteTeamMemberModalViewModel
  readonly removeTeamMemberDialogVm: RemoveTeamMemberDialogViewModel
  readonly pendingMembersVm: PendingTeamMembersViewModel

  constructor(
    public readonly team: Team,
    private readonly _teamRoleStore: TeamRoleStore,
    private readonly _teamMemberStore: TeamMemberStore,
    private readonly _invitationStore: InvitationStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this.inviteTeamMemberModalVm = new InviteTeamMemberModalViewModel(
      this._invitationStore,
      this._teamRoleStore,
      this.team,
      () => this.teamMembers,
      () => this._onInvitationSent(),
      this._createAlertErrorHandler,
    )

    this.removeTeamMemberDialogVm = new RemoveTeamMemberDialogViewModel(
      this._teamMemberStore,
      this._createAlertErrorHandler,
    )

    this.pendingMembersVm = new PendingTeamMembersViewModel(
      this.team,
      () => this.pendingTeamMembers,
      () => this.searchText,
    )
  }

  fetch = task(async () => {
    if (!this.canManageTeamMember) {
      return
    }
    const signal = this._lifecycle.signal
    await Promise.all([
      this._teamRoleStore.listWorkspaceTeamRoles(
        this.team.workspace.id,
        signal,
      ),
      this._teamMemberStore.listTeamMembers({ teamId: this.team.id }, signal),
      this._teamMemberStore.listPendingTeamMembers(
        { teamId: this.team.id, workspaceId: this.team.workspace.id },
        signal,
      ),
    ])
  })

  activate() {
    this._lifecycle.activate()
    void this.fetch().catch(ignoreAbortError())
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  setSearchText(query: string) {
    this.searchText = query
  }

  /**
   * Whether to show Current Members or Pending Members.
   */
  @action.bound
  setVisibleKind(memberKind: TeamMemberKind) {
    this.visibleKind = memberKind
  }

  /**
   * Team members.
   */
  @computed
  get teamMembers() {
    return this._teamMemberStore.forTeam(this.team.id)
  }

  @computed
  get pendingTeamMembers() {
    return this._teamMemberStore.pendingForTeam(this.team.id)
  }

  @computed
  get hasPendingMembers() {
    return this.pendingTeamMembers.length > 0
  }

  @computed
  get filtered() {
    let teamMembers = this.teamMembers

    if (this.searchText !== '') {
      teamMembers = this.teamMembers.filter(
        (teamMember) =>
          teamMember.member.familyName
            .toLowerCase()
            .includes(this.searchText.toLowerCase()) ||
          teamMember.teamRole.name
            .toLowerCase()
            .includes(this.searchText.toLowerCase()) ||
          teamMember.member.email?.includes(this.searchText.toLowerCase()),
      )
    }

    return orderBy(
      teamMembers,
      [this.sortField],
      this.sortOrder === 'none' ? undefined : this.sortOrder,
    )
  }

  @action.bound
  setSort(sortField: TeamMemberTableColumnKey, sortOrder: TableSortOrder) {
    this.sortField = sortField
    this.sortOrder = sortOrder
  }

  /**
   * Shows the invite modal
   */
  @action.bound
  showInviteModal() {
    return this.inviteTeamMemberModalVm.show(null)
  }

  /**
   * Available team roles.
   */
  @computed
  get teamRoles() {
    return this._teamRoleStore.forWorkspace(this.team.workspace.id)
  }

  @computed
  get canManageTeamMember() {
    const currentMember = this._getCurrentMember()
    if (currentMember.permissions.adminTeamsView) {
      return true
    }

    const currentTeamMember = this.teamMembers.find(
      (m) => m.member.user.id === currentMember.user.id,
    )

    return currentTeamMember?.permissions.manageTeamMember ?? false
  }

  /**
   * Changes the team member role.
   */
  @action.bound
  async changeTeamMemberRole(teamMember: TeamMember, newRoleId: string) {
    if (!this.canManageTeamMember) {
      return
    }

    if (teamMember.teamRole.id === newRoleId) {
      return
    }

    const newRole = this.teamRoles.find((r) => r.id === newRoleId)

    try {
      await this._teamMemberStore
        .updateTeamMember(
          {
            teamId: teamMember.team.id,
            userPublicId: teamMember.member.user.publicId,
          },
          { teamRoleId: newRoleId },
        )
        .catch(this._createAlertErrorHandler('Changing role failed'))
    } catch {
      return
    }

    this._newAlert()
      .withState('success')
      .saying(
        'Role Changed',
        `${teamMember.member.displayName} now has the ${
          newRole?.name ?? 'selected'
        } role`,
      )
      .autoDismiss()
      .show()
  }

  /**
   * Asks to confirm removing the team member.
   */
  @action.bound
  removeTeamMember(teamMember: TeamMember) {
    this.removeTeamMemberDialogVm.show(teamMember)
  }

  private async _onInvitationSent() {
    await this._teamMemberStore.listPendingTeamMembers(
      { teamId: this.team.id, workspaceId: this.team.workspace.id },
      this._lifecycle.signal,
    )

    this.setVisibleKind('Pending')
  }
}
